import { motion } from "framer-motion";
import { AppLayout } from "../../../layout/AppLayout";
import { UserContext } from "../../../components/context/UseContext";
import { useContext, useEffect, useRef } from "react";
import {
  Crown,
  Hands,
  Live,
  Profile,
} from "../../../components/svg/GeneralIcons";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import clsx from "clsx";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../../components/world/rmc/Header";
import { useGetBrandPrizes } from "../../../api/getBrandPrizes";
import { CardLoader } from "../../../components/loader/CardLoader";
import { useTriviaQuestions } from "../../../api/trivia/useTriviaQuestions";
import { RmcContext } from "../../../components/context/RmcContext";
import { FaLongArrowAltLeft, FaPlane } from "react-icons/fa";
import { useGenerateBrand } from "../../../api/useGenerateBrand";
import { Loader } from "../../../components/loader/Loader";

const responsive = {
  superLargeDesktop: {
    // the naming can be any, depends on you.
    breakpoint: { max: 4000, min: 3000 },
    items: 1,
    partialVisibilityGutter: 120,
  },
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 1,
    partialVisibilityGutter: 120,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 1,
    partialVisibilityGutter: 120,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
    partialVisibilityGutter: 120,
  },
};

function WorldHome() {
  const location = useLocation();
  const navigate = useNavigate();
  const hasFetched = useRef(false);
  const { token } = useContext(UserContext);
  const { getBrandPrizes, brandPrizes, loading } = useGetBrandPrizes();
  const { generateBrand, data, loading: isLoading } = useGenerateBrand();
  const { getTrivaCategories, loadingCategories, categories } =
    useTriviaQuestions();

  const { brandId, slug, worldBranch, setWorldBranch } = useContext(RmcContext);

  useEffect(() => {
    if (brandId) {
      generateBrand({
        token: token,
        brand_id: brandId,
        is_link: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, brandId]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const UrlBrandId = searchParams.get("id");

    if (token && UrlBrandId && !hasFetched.current) {
      hasFetched.current = true;
      getBrandPrizes(UrlBrandId, token);
      getTrivaCategories(UrlBrandId);
    } else if (token && !UrlBrandId && brandId && !hasFetched.current) {
      hasFetched.current = true;
      getBrandPrizes(brandId, token);
      getTrivaCategories(brandId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, location.search, brandId]);

  if (isLoading || loading) {
    return (
      <div className="h-[100dvh] bg-[#111522] flex items-center justify-center">
        <Loader type="spinningBubbles" color="#fff" />
      </div>
    );
  }

  return (
    <AppLayout
      bg={"!bg-[#111522]"}
      navBg={"!bg-[#111522]"}
      firstText={data?.data?.brand?.name ?? "Kingdom Quest"}
      secondText={"I'm Live"}
      thirdText={"Achievements"}
      fourthText={"Profile"}
      firstIcon={
        data?.data?.brand?.name?.toLowerCase().includes("airpeace") ? (
          <FaPlane className="w-5 h-5 -rotate-45" />
        ) : (
          <Crown className={"w-5 h-5"} />
        )
      }
      secondIcon={<Live className={"w-5 h-5"} />}
      fourthIcon={<Profile className={"w-5 h-5"} />}
      activeColor={data?.data?.brand?.secondary_color ?? "#FFD700"}
      inactiveClass={"text-[#FFFFFFBF]"}
      firstLink={`${slug}/home`}
      secondLink={`${slug}/live${
        worldBranch?.value ? `?branch_id=${worldBranch.value}` : ""
      }`}
      thirdLink={`${slug}/achievements`}
      fourthLink={`${slug}/profile`}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 3, delay: 0.4 }}
        className="relative pb-20"
      >
        <motion.div className="relative rounded-b-[25px]">
          <Header
            className="!pb-5"
            customBgColor={data?.data?.brand?.secondary_color}
          />
        </motion.div>
        <FaLongArrowAltLeft
          onClick={() => {
            navigate(`/worlds/${slug}?id=${brandId}`);
            setWorldBranch(null);
            localStorage.removeItem("world_branch");
          }}
          className="text-lg text-white ml-5 mb-3 mt-2  cursor-pointer"
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 3, delay: 0.4 }}
          className="px-5"
        >
          <motion.h3
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="tracking-tighter text-2xl font-semibold text-white mx-0"
          >
            {data?.data?.brand?.name ?? "Kingdom Quest Trivia"}
          </motion.h3>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 3, delay: 0.6 }}
            className="relative mt-5"
          >
            <img
              className="w-full object-contain rounded-[10px]"
              src={data?.data?.brand?.image_url ?? "/image/knight.jpg"}
              alt="banner"
              loading="lazy"
            />
          </motion.div>
          <motion.h3
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="tracking-tighter text-xl font-semibold mt-7 text-white mx-0"
          >
            Trivia Categories
          </motion.h3>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="text-xs font-medium mt-1 text-[#FFFFFF80]"
          >
            Select from the categories you’ll like to play.
          </motion.p>
          <div className={clsx("overflow-x-auto hidden-scrollbar")}>
            <div
              className={clsx(
                "min-w-fit max-w-[73rem] max-md:max-w-none mx-auto flex items-center overflow-x-scroll hidden-scrollbar",
              )}
            >
              <div className="flex items-center gap-4 mt-5 w-full">
                {loadingCategories ? (
                  <div className="flex items-center gap-4 w-full px-2">
                    <div className="h-11 bg-gray-600 rounded animate-pulse w-1/3"></div>
                    <div className="h-11 bg-gray-600 rounded animate-pulse w-1/3"></div>
                    <div className="h-11 bg-gray-600 rounded animate-pulse w-1/3"></div>
                  </div>
                ) : categories.length === 0 ? (
                  <div className=" w-full">
                    <div className="p-5 rounded-[10px] bg-[#FAFAFA] text-center">
                      <p className="text-[#00000080] font-semibold text-center text-sm">
                        You currently have no categories available.
                      </p>
                    </div>
                  </div>
                ) : (
                  categories.map((x, i) => {
                    return (
                      <div
                        onClick={() => {
                          navigate(
                            `/worlds/${slug}/rules?id=${brandId}&trivia_id=${x.id}`,
                          );
                        }}
                        key={i}
                        className="flex  flex-1 items-center gap-2 px-2 py-2 bg-[#1A1F33] border border-[#252B41] rounded-md cursor-pointer"
                      >
                        <Hands />{" "}
                        <span className="text-base font-semibold text-[#FFFFFFBF]">
                          {x?.name}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
          <motion.h3
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 2 }}
            className="capitalize tracking-tighter text-xl font-semibold mt-11 text-white mx-0"
          >
            Win Big in {data?.data?.brand?.name ?? "the Kingdom Quest"}
          </motion.h3>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 2.1 }}
            className="text-xs font-medium mt-1 text-[#FFFFFF80]"
          >
            Step into the adventure and stand a chance to win amazing prizes.
            Each correct answer brings you closer to victory. The more you play,
            the more you win!
          </motion.p>
        </motion.div>
        {loading ? (
          <div className="mt-5">
            <CardLoader
              count={2}
              className={"h-[90px]"}
              innerClass={"h-[90px]"}
            />
          </div>
        ) : brandPrizes?.data?.length > 0 ? (
          <Carousel
            autoPlay={false}
            responsive={responsive}
            arrows={false}
            partialVisible={true}
            ssr={true}
            className="mt-5"
          >
            {brandPrizes.data.map((x, i) => {
              const isEven = i % 2 === 0;
              return (
                <div
                  key={i}
                  className={clsx(
                    "p-5 rounded-[10px] h-[121px] flex items-center gap-5 justify-between mx-4",
                    isEven ? "bg-[#FAFAFA]" : "bg-[#F8DBD8]",
                  )}
                >
                  <div className="flex flex-col justify-between gap-2">
                    <p className="font-semibold text-sm text-[#000000] line-clamp-1">
                      {x.name}
                    </p>
                    <p className="text-xs leading-tight font-medium line-clamp-3 text-[#00000080]">
                      {x.description}
                    </p>
                  </div>
                  <img
                    className="w-[75px] h-[75px] rounded-[12px] object-cover"
                    src={x.image_url}
                    alt="banner"
                    loading="lazy"
                  />
                </div>
              );
            })}
          </Carousel>
        ) : (
          <div className="mt-5 px-4">
            <div className="p-5 rounded-[10px] bg-[#FAFAFA] text-center">
              <p className="text-[#00000080] text-sm">
                No prizes available at the moment
              </p>
            </div>
          </div>
        )}
      </motion.div>
    </AppLayout>
  );
}

export default WorldHome;
